import React from 'react';
import { Target, Eye, Heart, Users, Globe, Award } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const values = [
    { icon: Heart, title: 'Client-Centric', description: 'Your success is our priority' },
    { icon: Award, title: 'Excellence', description: 'Delivering quality in every project' },
    { icon: Users, title: 'Collaboration', description: 'Working together for better outcomes' },
    { icon: Globe, title: 'Innovation', description: 'Embracing cutting-edge technology' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full filter blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">About Gemini Tech Labs</h1>
            <p className="text-xl text-blue-100">
              Empowering businesses through intelligent automation and digital transformation since our inception
            </p>
          </div>
        </div>
      </section>

      {/* Company Story Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-gray-600 text-lg">
                <p>
                  Gemini Tech Labs was founded with a vision to revolutionize how businesses leverage technology. 
                  We recognized the growing need for comprehensive digital transformation solutions that go beyond 
                  simple software implementation.
                </p>
                <p>
                  Today, we stand as a global leader in ERP implementation, business automation, and digital 
                  transformation. Our team of certified consultants and technology experts work tirelessly to 
                  deliver solutions that drive real business value.
                </p>
                <p>
                  With a presence spanning across the United States, Middle East, and Southeast Asia, we've 
                  helped over 500 businesses transform their operations and achieve unprecedented growth.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740" 
                alt="Team collaboration" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-gradient-to-br from-blue-400 to-purple-600 rounded-2xl -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-none shadow-xl bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center mb-6">
                  <Target className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 text-lg">
                  To empower businesses worldwide with intelligent automation and digital transformation 
                  solutions that drive efficiency, innovation, and sustainable growth. We are committed 
                  to delivering excellence in every project and building long-term partnerships with our clients.
                </p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-xl bg-white">
              <CardContent className="p-10">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center mb-6">
                  <Eye className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 text-lg">
                  To become the world's most trusted partner for digital transformation, recognized for 
                  our innovation, expertise, and unwavering commitment to client success. We envision a 
                  future where every business, regardless of size, can harness the power of technology 
                  to achieve their full potential.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 text-center bg-gradient-to-br from-blue-50 to-white">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h3>
                    <p className="text-gray-600">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 via-blue-700 to-purple-700 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Global Presence</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Serving clients across three continents with localized expertise
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-2xl">
              <Globe className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">United States</h3>
              <p className="text-blue-100">Focus on automation and advanced integration solutions</p>
            </div>
            <div className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-2xl">
              <Globe className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Middle East</h3>
              <p className="text-blue-100">ERP implementation and data migration expertise</p>
            </div>
            <div className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-2xl">
              <Globe className="h-12 w-12 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">Southeast Asia</h3>
              <p className="text-blue-100">SME digital transformation specialists</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">Why Work With Us</h2>
            <div className="space-y-6">
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">1</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Industry Expertise</h3>
                  <p className="text-gray-600">Deep understanding of multiple industries and their unique challenges</p>
                </div>
              </div>
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">2</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Professionals</h3>
                  <p className="text-gray-600">Team of certified consultants with proven track records</p>
                </div>
              </div>
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">3</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">End-to-End Solutions</h3>
                  <p className="text-gray-600">From consultation to implementation and ongoing support</p>
                </div>
              </div>
              <div className="flex items-start p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">4</div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Proven ROI</h3>
                  <p className="text-gray-600">Measurable results and tangible business value from every project</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;